/**
 * Helpers for tracking the server session cookie's expiry on the client.
 *
 * The server creates a 14-day Firebase session cookie in /api/auth/create-session
 * and returns its expiresAt (ms since epoch). We persist this so the client can:
 *
 * 1. Detect when the cookie is about to expire and renew it proactively
 *    (before the server starts returning 401s).
 * 2. Keep the 14-day window rolling for active users by re-minting the cookie
 *    whenever Firebase refreshes the ID token (throttled).
 *
 * Storage is localStorage so it survives tab reloads; value is a plain number.
 */

const STORAGE_KEY = 'kb.sessionExpiresAt'

/** Margin before actual expiry at which we consider the session "due for refresh". */
export const SESSION_REFRESH_MARGIN_MS = 24 * 60 * 60 * 1000 // 1 day

/** Minimum interval between proactive session cookie renewals. */
export const SESSION_RENEWAL_MIN_INTERVAL_MS = 6 * 60 * 60 * 1000 // 6 hours

export function getStoredSessionExpiresAt(): number | null {
  if (!import.meta.client) return null
  try {
    const raw = localStorage.getItem(STORAGE_KEY)
    if (!raw) return null
    const n = Number(raw)
    return Number.isFinite(n) ? n : null
  }
  catch {
    return null
  }
}

export function setStoredSessionExpiresAt(expiresAt: number | null | undefined): void {
  if (!import.meta.client) return
  try {
    if (expiresAt && Number.isFinite(expiresAt)) {
      localStorage.setItem(STORAGE_KEY, String(expiresAt))
    }
    else {
      localStorage.removeItem(STORAGE_KEY)
    }
  }
  catch {
    // ignore storage errors (private mode, quota, etc.)
  }
}

export function clearStoredSessionExpiresAt(): void {
  setStoredSessionExpiresAt(null)
}

/**
 * True if we have no record of an expiry, or the recorded expiry is within
 * SESSION_REFRESH_MARGIN_MS of now (or already past).
 */
export function isSessionDueForRefresh(now: number = Date.now()): boolean {
  const expiresAt = getStoredSessionExpiresAt()
  if (expiresAt === null) return true
  return expiresAt - now <= SESSION_REFRESH_MARGIN_MS
}
